/* eslint-disable no-console */
/**
 * A5/A4 페이지 분할 유틸리티
 * - 콘텐츠를 페이지별로 분할
 * - 긴 텍스트 요소는 자동으로 분할하여 다음 페이지로 넘김
 */

/**
 * 텍스트 요소를 분할하는 헬퍼 함수
 * @param {HTMLElement} element - 분할할 요소
 * @param {number} availableHeight - 남은 높이 (px)
 * @param {number} contentWidth - 콘텐츠 너비 (px)
 * @param {string} fontSize - 폰트 크기
 * @param {string} lineHeight - 행간
 * @returns {{ firstPart: string, secondPart: string } | null}
 */
const splitTextElement = (element, availableHeight, contentWidth, fontSize, lineHeight) => {
  const tagName = element.tagName.toLowerCase();
  const text = element.textContent || '';

  // 텍스트가 없거나 분할 불가능한 요소면 null 반환
  // 제목(h1-h6), 표, 이미지, 그림은 분할하지 않음
  if (!text.trim() || ['table', 'img', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    return null;
  }

  // 참고문헌 관련 요소는 분할하지 않음
  if (element.classList && (
    element.classList.contains('reference-item') ||
    element.classList.contains('references-title') ||
    element.classList.contains('blockquote')
  )) {
    return null;
  }

  // 측정용 컨테이너 생성
  const measureDiv = document.createElement('div');
  measureDiv.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    visibility: hidden;
    width: ${contentWidth}px;
    font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
    font-size: ${fontSize};
    line-height: ${lineHeight};
  `;
  document.body.appendChild(measureDiv);

  // 문장 단위로 분할 시도 (마침표, 물음표, 느낌표 기준)
  const sentences = text.split(/(?<=[.?!。]\s*)/).filter(s => s.trim());

  if (sentences.length <= 1) {
    // 문장이 하나뿐이면 단어/글자 단위로 분할
    const chunks = text.split(/(\s+)/).filter(s => s);
    let firstPartText = '';
    let lastValidIndex = 0;

    for (let i = 0; i < chunks.length; i++) {
      const testText = chunks.slice(0, i + 1).join('');
      const testElement = element.cloneNode(false);
      testElement.textContent = testText;
      measureDiv.innerHTML = '';
      measureDiv.appendChild(testElement);

      if (measureDiv.offsetHeight > availableHeight) {
        break;
      }
      firstPartText = testText;
      lastValidIndex = i + 1;
    }

    document.body.removeChild(measureDiv);

    if (lastValidIndex > 0 && lastValidIndex < chunks.length) {
      const secondPartText = chunks.slice(lastValidIndex).join('').trim();
      if (secondPartText) {
        // 원본 요소의 속성 복사
        const firstEl = element.cloneNode(false);
        firstEl.textContent = firstPartText.trim();
        const secondEl = element.cloneNode(false);
        secondEl.textContent = secondPartText;
        secondEl.classList.add('text-continuation');

        return {
          firstPart: firstEl.outerHTML,
          secondPart: secondEl.outerHTML,
        };
      }
    }
    return null;
  }

  // 문장 단위로 분할
  let firstPartSentences = [];
  let lastValidIndex = 0;

  for (let i = 0; i < sentences.length; i++) {
    const testText = sentences.slice(0, i + 1).join('');
    const testElement = element.cloneNode(false);
    testElement.textContent = testText;
    measureDiv.innerHTML = '';
    measureDiv.appendChild(testElement);

    if (measureDiv.offsetHeight > availableHeight) {
      break;
    }
    firstPartSentences = sentences.slice(0, i + 1);
    lastValidIndex = i + 1;
  }

  document.body.removeChild(measureDiv);

  if (lastValidIndex > 0 && lastValidIndex < sentences.length) {
    const firstText = firstPartSentences.join('').trim();
    const secondText = sentences.slice(lastValidIndex).join('').trim();

    if (firstText && secondText) {
      const firstEl = element.cloneNode(false);
      firstEl.textContent = firstText;
      const secondEl = element.cloneNode(false);
      secondEl.textContent = secondText;
      secondEl.classList.add('text-continuation');

      return {
        firstPart: firstEl.outerHTML,
        secondPart: secondEl.outerHTML,
      };
    }
  }

  return null;
};

export const getSplitContentByHeight = ({
  content,
  pageHeight = 794,
  pageWidth = 559,
  verticalPadding = 50,
  horizontalPadding = 75,
  footerHeight = 30,
  templateRef = null,
  pageSize = 'a5', // 페이지 크기 (a5 또는 a4)
}) => {
  if (!content || content.trim() === '') {
    return [''];
  }

  // 사용 가능한 콘텐츠 영역 높이 계산
  const contentHeight = pageHeight - (verticalPadding * 2) - footerHeight;
  const contentWidth = pageWidth - (horizontalPadding * 2);

  // 페이지 크기에 따른 스타일 설정 (가이드 기준)
  // A5: 본문 9pt/행간 15pt, A4: 본문 12pt/행간 20pt
  const fontSize = pageSize === 'a4' ? '12pt' : '9pt';
  const lineHeight = pageSize === 'a4' ? '20pt' : '15pt';

  // 임시 컨테이너 생성
  const tempContainer = document.createElement('div');
  tempContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    visibility: hidden;
    width: ${contentWidth}px;
    font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
    font-size: ${fontSize};
    line-height: ${lineHeight};
  `;
  tempContainer.innerHTML = content;
  document.body.appendChild(tempContainer);

  // 자식 요소들 가져오기
  const children = Array.from(tempContainer.children);

  console.log('[heightCalculator] Total children:', children.length);
  // 참고문헌 관련 요소 확인 (상세 디버깅)
  children.forEach((child, idx) => {
    const hasRefClass = child.classList && child.classList.contains('references-title');
    const textContent = child.textContent?.trim() || '';
    const isRefText = textContent === '참고문헌' || textContent === '참고 문헌' || textContent === 'References';

    if (hasRefClass) {
      console.log(`[heightCalculator] Child ${idx} HAS references-title class:`, child.outerHTML.substring(0, 100));
    }
    if (isRefText) {
      console.log(`[heightCalculator] Child ${idx} text matches 참고문헌:`, child.tagName, `class="${child.className}"`, `text="${textContent}"`);
    }
    if (textContent.includes('참고문헌') && !isRefText) {
      console.log(`[heightCalculator] Child ${idx} CONTAINS 참고문헌 but not exact:`, child.tagName, `class="${child.className}"`, `text="${textContent.substring(0, 50)}..."`);
    }
  });

  if (children.length === 0) {
    document.body.removeChild(tempContainer);
    return [content];
  }

  // 요소 높이 측정 함수
  const measureElementHeight = (element) => {
    const measureDiv = document.createElement('div');
    measureDiv.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      visibility: hidden;
      width: ${contentWidth}px;
      font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
      font-size: ${fontSize};
      line-height: ${lineHeight};
    `;
    document.body.appendChild(measureDiv);

    const clone = element.cloneNode(true);
    measureDiv.appendChild(clone);

    const computedStyle = window.getComputedStyle(clone);
    const height = clone.offsetHeight +
      parseFloat(computedStyle.marginTop || 0) +
      parseFloat(computedStyle.marginBottom || 0);

    document.body.removeChild(measureDiv);
    return height;
  };

  const pages = [];
  let currentPageContent = [];
  let currentHeight = 0;
  let pendingElements = [...children]; // 처리할 요소들

  // 제목 태그인지 확인하는 함수 (참고문헌 제목 포함)
  const isHeadingElement = (el) => {
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toLowerCase();
    // h1-h6 태그 또는 references-title 클래스
    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return true;
    if (el.classList && el.classList.contains('references-title')) return true;
    return false;
  };

  // 제목 뒤에 최소 몇 줄이 따라와야 하는 높이 (약 3줄 분량)
  const minContentAfterHeading = 60; // px

  while (pendingElements.length > 0) {
    const child = pendingElements.shift();

    // 페이지 나누기 처리 (강제 분할)
    if (child.classList && child.classList.contains('page-break')) {
      if (currentPageContent.length > 0) {
        pages.push(currentPageContent.join(''));
        currentPageContent = [];
        currentHeight = 0;
      }
      continue;
    }

    // 참고문헌은 항상 새 페이지에서 시작
    // 클래스로 감지 또는 텍스트 내용으로 감지 (이전 저장된 콘텐츠 호환)
    const hasRefClass = child.classList && child.classList.contains('references-title');
    const textTrimmed = child.textContent?.trim() || '';
    const isRefByText = (
      textTrimmed === '참고문헌' ||
      textTrimmed === '참고 문헌' ||
      textTrimmed === 'References' ||
      /^참고\s*문헌\s*$/.test(textTrimmed)
    );
    const isReferencesTitle = hasRefClass || isRefByText;

    // 디버그: 참고문헌 텍스트 포함 여부 확인
    if (textTrimmed.includes('참고문헌')) {
      console.log('[heightCalculator] Processing element with 참고문헌:', {
        tag: child.tagName,
        class: child.className,
        hasRefClass,
        isRefByText,
        isReferencesTitle,
        textLength: textTrimmed.length,
        textPreview: textTrimmed.substring(0, 50)
      });
    }

    if (isReferencesTitle) {
      console.log('[heightCalculator] ✓ DETECTED references-title, starting new page:', textTrimmed.substring(0, 30));
      if (currentPageContent.length > 0) {
        pages.push(currentPageContent.join(''));
        currentPageContent = [];
        currentHeight = 0;
      }
      // 참고문헌 제목을 새 페이지 첫 요소로 추가 (클래스 추가하여 스타일 적용)
      const clonedChild = child.cloneNode(true);
      if (!clonedChild.classList.contains('references-title')) {
        clonedChild.classList.add('references-title');
      }
      const elementHeight = measureElementHeight(clonedChild);
      currentPageContent.push(clonedChild.outerHTML);
      currentHeight = elementHeight;
      continue;
    }

    const elementHeight = measureElementHeight(child);
    const availableHeight = contentHeight - currentHeight;

    // 제목 요소인 경우 특별 처리
    if (isHeadingElement(child)) {
      // 다음 요소가 있는지 확인
      const nextElement = pendingElements[0];
      const nextElementHeight = nextElement ? measureElementHeight(nextElement) : 0;

      // 제목 + 다음 요소의 최소 높이가 남은 공간에 안 들어가면 다음 페이지로
      const minRequiredHeight = elementHeight + Math.min(nextElementHeight, minContentAfterHeading);

      if (minRequiredHeight > availableHeight && currentPageContent.length > 0) {
        // 현재 페이지 완료하고 제목은 다음 페이지로
        pages.push(currentPageContent.join(''));
        currentPageContent = [child.outerHTML];
        currentHeight = elementHeight;
        continue;
      }

      // 제목이 들어갈 수 있으면 추가
      if (elementHeight <= availableHeight) {
        currentPageContent.push(child.outerHTML);
        currentHeight += elementHeight;
        continue;
      }
    }

    // 현재 페이지에 추가 가능한지 확인
    if (elementHeight <= availableHeight) {
      // 남은 공간에 들어감
      currentPageContent.push(child.outerHTML);
      currentHeight += elementHeight;
    } else if (currentPageContent.length === 0 && elementHeight > contentHeight) {
      // 요소가 페이지 전체보다 큼 - 분할 필요
      const splitResult = splitTextElement(child, contentHeight, contentWidth, fontSize, lineHeight);

      if (splitResult) {
        // 분할 성공
        currentPageContent.push(splitResult.firstPart);
        pages.push(currentPageContent.join(''));
        currentPageContent = [];
        currentHeight = 0;

        // 나머지 부분을 새 요소로 만들어서 대기열 앞에 추가
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = splitResult.secondPart;
        if (tempDiv.firstElementChild) {
          pendingElements.unshift(tempDiv.firstElementChild);
        }
      } else {
        // 분할 불가 - 그냥 새 페이지에 추가
        currentPageContent.push(child.outerHTML);
        currentHeight += elementHeight;
      }
    } else if (currentPageContent.length > 0 && elementHeight > availableHeight) {
      // 남은 공간에 안 들어가지만, 요소 분할 시도
      const splitResult = splitTextElement(child, availableHeight, contentWidth, fontSize, lineHeight);

      if (splitResult) {
        // 분할 성공 - 첫 부분은 현재 페이지에
        currentPageContent.push(splitResult.firstPart);
        pages.push(currentPageContent.join(''));
        currentPageContent = [];
        currentHeight = 0;

        // 나머지 부분을 대기열 앞에 추가
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = splitResult.secondPart;
        if (tempDiv.firstElementChild) {
          pendingElements.unshift(tempDiv.firstElementChild);
        }
      } else {
        // 분할 불가 - 현재 페이지 완료하고 새 페이지에 추가
        pages.push(currentPageContent.join(''));
        currentPageContent = [child.outerHTML];
        currentHeight = elementHeight;
      }
    }
  }

  // 마지막 페이지 추가
  if (currentPageContent.length > 0) {
    pages.push(currentPageContent.join(''));
  }

  document.body.removeChild(tempContainer);

  return pages.length > 0 ? pages : [content];
};

// Legacy 함수 (하위 호환성)
export const splitContentByLine = (
  content,
  pageHeight,
  lineHeight,
  pageWidth
) => {
  return getSplitContentByHeight({
    content,
    pageHeight,
    pageWidth,
    verticalPadding: 50,
    horizontalPadding: 75,
  });
};
